import {
  Injectable,
  Inject,
  autobind,
  forwardRef,
} from '@kaokei/use-vue-service';
import { DaoService } from '../dao.service';

import { ExamService } from '@/services/exam/exam.service';

import { UserService } from '@/services/user.service';
import { CheatService } from '@/services/exam/cheat.service';
import { CountdownService } from '@/services/exam/countdown.service';
import TRTC from 'trtc-sdk-v5';
import { throttle } from 'lodash-es';
import { logger } from '@/utils';

if (process.env.NODE_ENV === 'development') {
  TRTC.setLogLevel(5);
}
// 试卷信息和用户答案和用户信息
@Injectable()
export class TrtcService {
  @Inject(forwardRef(() => ExamService))
  public examService!: ExamService;

  @Inject(CheatService)
  public cheatService!: CheatService;

  @Inject(CountdownService)
  public countdownService!: CountdownService;

  public trtc!: TRTC;
  public videoStream!: MediaStream;
  public audioStream!: MediaStream;
  public showScreenShareTips = false;
  public showH5LiveScreen = false;
  // 是否添加阻断行为
  public isAddBlocked = true;
  // 是否正常展示三方视角提示按钮
  public isShowH5GuideBtn = false;

  public tryTimer: NodeJS.Timeout | null = null;

  constructor(public daoService: DaoService, public userService: UserService) {}

  public get userIdVal() {
    const examUId =
      this.examService?.userInfo4Exam?.userId ||
      this.examService.userService.user?.userId;
    const sessionUserId = window.sessionStorage.getItem('pc_contest_user_id');
    logger.info('userIdVal ==>', examUId, sessionUserId);
    return examUId || Number(sessionUserId) || 0;
  }

  // 设置是否添加阻断行为表示
  @autobind
  public setAddBlocked(flag = true) {
    this.isAddBlocked = flag;
  }

  @autobind
  public async getUserIsInRoom(platform = 'mobile') {
    if (!this.examService.contestInfoId) {
      return false;
    }
    return this.daoService.getUserIsInRoom({
      userExamId: this.examService.userExamId,
      contestInfoId: this.examService.contestInfoId,
      platform,
    });
  }

  @autobind
  public async getPcRoomSig() {
    if (!this.examService.contestInfoId) {
      return false;
    }
    return this.daoService.getTrtcRoomInfo({
      userExamId: this.examService.userExamId,
      contestInfoId: this.examService.contestInfoId,
      platform: 'pc',
    });
  }

  @autobind
  public async enterPcRoom() {
    const attemptEnter = async (attempt: number) => {
      const res = await this.getPcRoomSig();
      if (!res) {
        return false;
      }
      // 如果尚未创建，则创建 TRTC 实例；失败重试时复用实例
      if (!this.trtc) {
        this.trtc = TRTC.create();
      }
      try {
        await this.trtc.enterRoom({
          strRoomId: res.roomId as string,
          sdkAppId: res.sdkAppId,
          userId: `pc_${this.userIdVal}`,
          userSig: res.userSig,
          autoReceiveAudio: false,
        });
        this.isShowH5GuideBtn = true;
        // 仅在进房成功后进行初始化
        this.initAfterEnter();
        return true;
      } catch (error) {
        const err = error as any;
        this.eventTrack({
          elementName: '进房异常',
          error_message:
            err?.message ?? (err instanceof Error ? err.message : String(err)),
          statusText: `${err?.code ?? 'UNKNOWN'}@${err?.extraCode ?? ''}`,
        });
        // 异常时随机 0.5s 到 3s 重试，最多三次
        if (attempt < 3) {
          const delay = 500 + Math.floor(Math.random() * 2500);
          this.tryTimer = setTimeout(() => {
            attemptEnter(attempt + 1);
          }, delay);
        } else {
          // 给一个进房的假象 因为要执行ai检测需要视频流
          this.initAfterEnter();
        }
        return false;
      }
    };

    // 首次尝试
    attemptEnter(0);
  }

  @autobind
  public async initAfterEnter() {
    this.initScreenShare();
    this.trtc.startLocalVideo();
    this.trtc.startLocalAudio({
      option: {
        earMonitorVolume: 0,
      },
    });
    this.initEvent();
  }

  @autobind
  public initScreenShare() {
    this.cheatService.disable();
    this.trtc
      .startScreenShare()
      .then(() => {
        this.showScreenShareTips = false;
        this.isInH5LiveRoom();
      })
      .catch(() => {
        if (this.countdownService.remainTime > 0) {
          this.showScreenShareTips = true;
        }
      })
      .finally(() => {
        this.cheatService.enable();
      });
  }

  @autobind
  public async isInH5LiveRoom() {
    // 这里新的使用trtc功能的要获取用户是否在三方视角的房间
    const res = await this.getUserIsInRoom();
    if (!res && this.isAddBlocked && this.countdownService.remainTime > 0) {
      this.showH5LiveScreen = true;
    }
    return res;
  }

  @autobind
  public async exitRoom() {
    if (this.trtc) {
      await this.trtc.stopLocalAudio();
      await this.trtc.stopLocalVideo();
      this.trtc.exitRoom();
      this.trtc.destroy();
      this.showScreenShareTips = false;
      this.showH5LiveScreen = false;
    }
    if (this.tryTimer !== null) {
      clearTimeout(this.tryTimer);
    }
  }

  @autobind
  public initEvent() {
    const trtc = this.trtc;
    trtc.on(TRTC.EVENT.TRACK, event => {
      const kind = event.track.kind;
      const streamType = event.streamType;
      if (kind === 'video' && streamType === TRTC.TYPE.STREAM_TYPE_MAIN) {
        this.videoStream = new MediaStream([event.track]);
      }
      if (kind === 'audio') {
        this.audioStream = new MediaStream([event.track]);
      }
    });
    trtc.on(TRTC.EVENT.ERROR, error => {
      this.eventTrack({
        elementName: '异常',
        statusText: error.code + '@' + error.extraCode,
        error_message: error.message,
      });
    });
    trtc.on(TRTC.EVENT.SCREEN_SHARE_STOPPED, () => {
      this.eventTrack({
        elementName: '屏幕分享已停止',
      });
      this.showScreenShareTips = true;
    });
    trtc.on(TRTC.EVENT.REMOTE_USER_EXIT, event => {
      const userId = event.userId;
      const curUserId = this.examService.userService.user?.userId || 0;
      const mobileUserId = `mobile_${curUserId || ''}`;
      logger.info('三方视角用户退出房间', userId);
      if (
        curUserId &&
        mobileUserId === userId &&
        this.countdownService.remainTime > 0
      ) {
        this.showH5LiveScreen = true;
        this.eventTrack({
          elementName: '三方视角用户退出房间',
        });
      }
    });
    trtc.on(TRTC.EVENT.REMOTE_USER_ENTER, event => {
      const userId = event.userId;
      const curUserId = this.examService.userService.user?.userId || 0;
      const mobileUserId = `mobile_${curUserId || ''}`;
      if (curUserId && mobileUserId === userId) {
        this.showH5LiveScreen = false;
        this.eventTrack({
          elementName: '三方视角用户进入房间',
        });
      }
    });
    trtc.on(
      TRTC.EVENT.NETWORK_QUALITY,
      throttle(event => {
        if (event.uplinkNetworkQuality > 3) {
          this.eventTrack({
            elementName: '网络质量异常',
            mode: 'uplinkNetworkQuality',
            start_time: Date.now(),
            type: event.uplinkNetworkQuality,
            error_message: `uplink rtt:${event.uplinkRTT} loss:${event.uplinkLoss}`,
          });
        }
        if (event.downlinkNetworkQuality > 3) {
          this.eventTrack({
            elementName: '网络质量异常',
            mode: 'downlinkNetworkQuality',
            start_time: Date.now(),
            type: event.downlinkNetworkQuality,
            error_message: `downlink rtt:${event.downlinkRTT} loss:${event.downlinkLoss}`,
          });
        }
      }, 1000 * 60)
    );
  }

  @autobind
  public eventTrack(data: Record<string, any>, type = 'element_show') {
    window.hetaoTrack(type, {
      pageName: '赛考页面',
      userId: this.userIdVal,
      examId: this.examService.userExamId ?? -1,
      ...data,
    });
  }

  @autobind
  public mergeAudioVideo(): MediaStream | null {
    if (!this.videoStream || !this.audioStream) {
      return null;
    }

    // 创建新的合并流
    const mergedStream = new MediaStream();

    // 添加视频轨道
    this.videoStream.getVideoTracks().forEach(track => {
      mergedStream.addTrack(track);
    });

    // 添加音频轨道
    this.audioStream.getAudioTracks().forEach(track => {
      mergedStream.addTrack(track);
    });

    return mergedStream;
  }
}
